import { Link } from "react-router-dom";
import { ArrowUpRight, ExternalLink, Trophy } from "lucide-react";
import { Reveal, SectionHeading } from "../components/Motion";
import { TIMELINE, EXPERTISE_GROUPS, LINKS, useContent } from "../lib/content";

const MANIFESTO = [
  { n: "01", title: "Clarity before code", body: "Every engagement starts with the business problem, not the technology. AI is a lever - not the goal." },
  { n: "02", title: "Numbers executives trust", body: "TCO, NPV, IRR. If value can't be defended in front of a CFO, it isn't value yet." },
  { n: "03", title: "Production over pilots", body: "Demos are easy. I architect for governance, fault tolerance, and adoption - so systems survive contact with reality." },
];

export const HowIThinkSection = () => (
  <section data-testid="how-i-think-section" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32">
    <SectionHeading
      overline="How I Think"
      title={<>Three principles behind <span className="text-gradient">every engagement</span></>}
    />
    <div className="grid md:grid-cols-3 gap-6 md:gap-8">
      {MANIFESTO.map((m, i) => (
        <Reveal key={m.n} delay={i * 0.1}>
          <div data-testid={`manifesto-card-${i}`} className="border-t border-border pt-8 h-full">
            <span className="font-mono text-sm text-secondary">{m.n}</span>
            <h3 className="mt-4 font-display text-xl md:text-2xl font-medium tracking-tight">{m.title}</h3>
            <p className="mt-3 text-muted-foreground leading-relaxed text-sm md:text-base">{m.body}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

export const AboutSection = ({ full = false }) => (
  <section data-testid="about-section" className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32">
    {!full && (
      <SectionHeading
        overline="About - The Consultant"
        title={<>Advisor to organizations navigating <span className="text-gradient">AI transformation</span></>}
        description="AI Solutions Consultant with 6+ years driving AI-enabled analytics, intelligent automation, and enterprise transformation - experienced in LLMs, RAG, Conversational AI, MCP, and Business Intelligence."
      />
    )}

    <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
      <div className="lg:col-span-5">
        <Reveal>
          <div className="relative rounded-2xl overflow-hidden border border-border bg-gradient-to-br from-muted/60 to-background">
            <picture>
              <source srcSet="/yash-bhatt.webp" type="image/webp" />
              <img
                src="/yash-bhatt.jpg"
                alt="Yash Bhatt, AI Strategy Consultant"
                className="w-full h-[520px] object-contain"
                style={{ objectPosition: "center center" }}
                loading="lazy"
              />
            </picture>
            <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-transparent to-transparent" />
            <div className="absolute bottom-0 p-6">
              <p className="font-display text-lg font-semibold">Yash Anand Bhatt</p>
              <p className="text-sm text-muted-foreground">Bangalore, India · Working globally</p>
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.1} className="mt-10">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-secondary mb-4">Journey</p>
          <p className="text-muted-foreground leading-relaxed text-sm md:text-base">
            From financial research at S&P Global and forecasting automation at Oceaneering, to consultative research at Google
            and enterprise AI consulting at Axion Ray - my career has been one continuous thread: translating complex data
            into decisions organizations can act on.
          </p>
        </Reveal>
        <Reveal delay={0.15} className="mt-8" data-testid="mission-vision-block">
          <div className="rounded-xl border border-border bg-card p-5">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-secondary mb-2">Mission & Vision</p>
            <p className="text-sm md:text-base text-foreground/85 leading-relaxed">Make enterprise AI adoption measurable, governed, and genuinely useful - so every strategic decision is backed by defensible analysis, not hype.</p>
          </div>
        </Reveal>
        <Reveal delay={0.2} className="mt-8">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-secondary mb-4">Values</p>
          <div className="flex flex-wrap gap-2" data-testid="values-block">
            {["Clarity", "Rigor", "Ownership", "Measurable Outcomes", "Integrity"].map((v) => (
              <span key={v} className="px-4 py-1.5 rounded-full border border-border bg-card text-xs font-medium text-foreground/80">{v}</span>
            ))}
          </div>
        </Reveal>
      </div>

      <div className="lg:col-span-7">
        {full && (
          <>
            <Reveal>
              <p className="font-mono text-xs uppercase tracking-[0.25em] text-secondary mb-8">How I Think</p>
            </Reveal>
            <div className="space-y-10 mb-16">
              {MANIFESTO.map((m, i) => (
                <Reveal key={m.n} delay={i * 0.1}>
                  <div className="flex gap-6 md:gap-10 items-start group">
                    <span className="font-mono text-sm text-secondary pt-1 shrink-0">{m.n}</span>
                    <div className="border-b border-border pb-8 flex-1">
                      <h3 className="font-display text-xl md:text-2xl font-medium tracking-tight">{m.title}</h3>
                      <p className="mt-3 text-muted-foreground leading-relaxed text-sm md:text-base">{m.body}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </>
        )}
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-secondary mb-8">Experience & Timeline</p>
        </Reveal>
        <div className="relative pl-8 border-l border-border space-y-10">
          {TIMELINE.map((t, i) => (
            <Reveal key={t.org} delay={i * 0.08}>
              <div className="relative">
                <span className="absolute -left-[37px] top-1.5 h-2.5 w-2.5 rounded-full bg-gradient-to-r from-violet-500 to-blue-500 ring-4 ring-background" />
                <p className="font-mono text-xs text-muted-foreground">{t.year}</p>
                <h4 className="font-display text-lg font-medium mt-1">{t.role} · <span className="text-secondary">{t.org}</span></h4>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{t.detail}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>

    {!full && (
      <Reveal className="mt-16">
        <Link to="/about" data-testid="about-more-link" className="inline-flex items-center gap-2 text-sm text-secondary hover:underline">
          Full story & credentials <ArrowUpRight size={14} />
        </Link>
      </Reveal>
    )}
  </section>
);

export const ExpertiseGrid = () => (
  <section data-testid="expertise-section" className="max-w-7xl mx-auto px-6 lg:px-10 py-20 md:py-24">
    <SectionHeading
      overline="Expertise"
      title="A rare blend of strategy, engineering & storytelling"
    />
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6 -mt-6">
      {EXPERTISE_GROUPS.map((g, gi) => (
        <Reveal key={g.group} delay={gi * 0.08} y={24}>
          <div data-testid={`expertise-group-${gi}`} className="card-hover rounded-2xl border border-border bg-card p-6 md:p-7 h-full hover:border-secondary/40">
            <h3 className="font-display text-base md:text-lg font-medium tracking-tight text-gradient">{g.group}</h3>
            <div className="mt-5 flex flex-wrap gap-2">
              {g.skills.map((s) => (
                <span key={s} className="px-3.5 py-1.5 rounded-full border border-border bg-background text-xs md:text-sm font-medium text-foreground/80 hover:border-secondary/50 hover:text-secondary transition-colors duration-200 cursor-default">
                  {s}
                </span>
              ))}
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

export const AchievementsSection = () => {
  const { achievements } = useContent();
  if (!achievements?.length) return null;
  return (
    <section data-testid="achievements-section" className="border-y border-border bg-muted/30">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 md:py-32">
        <SectionHeading
          overline="Achievements"
          title={<>Results that made it to <span className="text-gradient">board decks</span></>}
          description="A track record of quantified outcomes across enterprise AI, analytics, and financial transformation."
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {achievements.map((a, i) => (
            <Reveal key={a.id} delay={(i % 3) * 0.08}>
              <div data-testid={`achievement-card-${i}`} className="card-hover group rounded-2xl border border-border bg-card p-8 h-full hover:border-secondary/40 relative overflow-hidden">
                <div className="flex items-start justify-between">
                  <p className="font-display text-4xl md:text-5xl font-medium tracking-tighter text-gradient">{a.stat}</p>
                  <Trophy size={17} strokeWidth={1.6} className="text-secondary/50 mt-2" />
                </div>
                <h3 className="mt-5 font-display text-lg font-medium tracking-tight">{a.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{a.detail}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export const EducationBlock = () => {
  const { education } = useContent();
  return (
    <section data-testid="education-section" className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
      <Reveal>
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-secondary mb-8">Education & Certifications</p>
      </Reveal>
      <div className="grid md:grid-cols-3 gap-4">
        {education.map((e, i) => {
          const inner = (
            <>
              <div className="flex items-start justify-between">
                {e.logo ? (
                  <span className="inline-flex items-center justify-center rounded-lg bg-white p-2 h-12 max-w-[160px] border border-border/50">
                    <img src={e.logo} alt={`${e.school} logo`} className="h-8 w-auto object-contain" loading="lazy" />
                  </span>
                ) : (
                  <p className="font-mono text-xs text-muted-foreground">{e.year}</p>
                )}
                {e.link && <ExternalLink size={14} className="text-secondary" />}
              </div>
              {e.logo && <p className="mt-3 font-mono text-xs text-muted-foreground">{e.year}</p>}
              <p className="mt-2 font-display font-medium">{e.degree}</p>
              <p className="mt-1 text-sm text-muted-foreground">{e.school}</p>
              {e.link && <p className="mt-3 text-xs text-secondary font-medium">View credential</p>}
            </>
          );
          const cls = "rounded-xl border border-border bg-card p-6 h-full block card-hover" + (e.link ? " hover:border-secondary/40" : "");
          return (
            <Reveal key={e.id} delay={i * 0.1}>
              {e.link ? (
                <a href={e.link} target="_blank" rel="noreferrer" data-testid={`education-card-${i}`} className={cls}>{inner}</a>
              ) : (
                <div data-testid={`education-card-${i}`} className={cls}>{inner}</div>
              )}
            </Reveal>
          );
        })}
      </div>
      <Reveal className="mt-10">
        <a href={`mailto:${LINKS.email}?subject=Resume Request`} className="text-sm text-secondary hover:underline inline-flex items-center gap-2" data-testid="resume-request-link">
          Request full resume <ArrowUpRight size={14} />
        </a>
      </Reveal>
    </section>
  );
};
